import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { AbstractControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MdbDatepickerModule, MdbDatepickerOptions } from 'mdb-angular-ui-kit/datepicker';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbValidationModule } from 'mdb-angular-ui-kit/validation';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-datepicker',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    FormsModule,
    MdbDatepickerModule,
    MdbFormsModule,
    ReactiveFormsModule,
    MdbValidationModule,
    TranslateModule
],
    templateUrl: './datepicker.component.html',
    standalone: true,
    styleUrl: './datepicker.component.scss'
})
export class DatepickerComponent {
  id = input.required<string>();
  label = input.required<string>();
  controlName = input.required<string>();
  formGroup = input.required<FormGroup>();
  isRequired = input(false);
  placeholder = input<string>();

  constructor(private translate: TranslateService) {
  }

  get control(): AbstractControl | null {
    return this.formGroup().get(this.controlName());
  }

  getDatePickerOptions() {
    let options: MdbDatepickerOptions = {
      cancelBtnText: this.translate.instant("DATEPICKER.CANCEL"),
      clearBtnText: this.translate.instant("DATEPICKER.CLEAR"),
      okBtnText: this.translate.instant("DATEPICKER.OK"),
      title: this.translate.instant("DATEPICKER.SELECT_DATE"),
      weekdaysShort: [
        this.translate.instant("DATEPICKER.WEEKDAYS_SHORT.SUN"),
        this.translate.instant("DATEPICKER.WEEKDAYS_SHORT.MON"),
        this.translate.instant("DATEPICKER.WEEKDAYS_SHORT.TUE"),
        this.translate.instant("DATEPICKER.WEEKDAYS_SHORT.WED"),
        this.translate.instant("DATEPICKER.WEEKDAYS_SHORT.THU"),
        this.translate.instant("DATEPICKER.WEEKDAYS_SHORT.FRI"),
        this.translate.instant("DATEPICKER.WEEKDAYS_SHORT.SAT"),
      ],
      weekdaysFull: [
        this.translate.instant("DATEPICKER.WEEKDAYS_FULL.SUNDAY"),
        this.translate.instant("DATEPICKER.WEEKDAYS_FULL.MONDAY"),
        this.translate.instant("DATEPICKER.WEEKDAYS_FULL.TUESDAY"),
        this.translate.instant("DATEPICKER.WEEKDAYS_FULL.WEDNESDAY"),
        this.translate.instant("DATEPICKER.WEEKDAYS_FULL.THURSDAY"),
        this.translate.instant("DATEPICKER.WEEKDAYS_FULL.FRIDAY"),
        this.translate.instant("DATEPICKER.WEEKDAYS_FULL.SATURDAY"),
      ],
      weekdaysNarrow: [
        this.translate.instant("DATEPICKER.WEEKDAYS_NARROW.SUN"),
        this.translate.instant("DATEPICKER.WEEKDAYS_NARROW.MON"),
        this.translate.instant("DATEPICKER.WEEKDAYS_NARROW.TUE"),
        this.translate.instant("DATEPICKER.WEEKDAYS_NARROW.WED"),
        this.translate.instant("DATEPICKER.WEEKDAYS_NARROW.THU"),
        this.translate.instant("DATEPICKER.WEEKDAYS_NARROW.FRI"),
        this.translate.instant("DATEPICKER.WEEKDAYS_NARROW.SAT"),
      ],
      monthsFull: [
        this.translate.instant("DATEPICKER.MONTHS_FULL.JANUARY"),
        this.translate.instant("DATEPICKER.MONTHS_FULL.FEBRUARY"),
        this.translate.instant("DATEPICKER.MONTHS_FULL.MARCH"),
        this.translate.instant("DATEPICKER.MONTHS_FULL.APRIL"),
        this.translate.instant("DATEPICKER.MONTHS_FULL.MAY"),
        this.translate.instant("DATEPICKER.MONTHS_FULL.JUNE"),
        this.translate.instant("DATEPICKER.MONTHS_FULL.JULY"),
        this.translate.instant("DATEPICKER.MONTHS_FULL.AUGUST"),
        this.translate.instant("DATEPICKER.MONTHS_FULL.SEPTEMBER"),
        this.translate.instant("DATEPICKER.MONTHS_FULL.OCTOBER"),
        this.translate.instant("DATEPICKER.MONTHS_FULL.NOVEMBER"),
        this.translate.instant("DATEPICKER.MONTHS_FULL.DECEMBER"),
      ],
      monthsShort: [
        this.translate.instant("DATEPICKER.MONTHS_SHORT.JAN"),
        this.translate.instant("DATEPICKER.MONTHS_SHORT.FEB"),
        this.translate.instant("DATEPICKER.MONTHS_SHORT.MAR"),
        this.translate.instant("DATEPICKER.MONTHS_SHORT.APR"),
        this.translate.instant("DATEPICKER.MONTHS_SHORT.MAY"),
        this.translate.instant("DATEPICKER.MONTHS_SHORT.JUN"),
        this.translate.instant("DATEPICKER.MONTHS_SHORT.JUL"),
        this.translate.instant("DATEPICKER.MONTHS_SHORT.AUG"),
        this.translate.instant("DATEPICKER.MONTHS_SHORT.SEP"),
        this.translate.instant("DATEPICKER.MONTHS_SHORT.OCT"),
        this.translate.instant("DATEPICKER.MONTHS_SHORT.NOV"),
        this.translate.instant("DATEPICKER.MONTHS_SHORT.DEC"),
      ]
    }

    return options;
  }

  protected readonly environment = environment;

  /**
   * Syncs the selected date into the form control when the user picks a date.
   * MDB datepicker may not update the reactive form control when the input is readonly.
   */
  onDateChange(date: Date | null): void {
    const ctrl = this.control;
    if (!ctrl) return;
    if (date == null) {
      ctrl.setValue('', { emitEvent: true });
      return;
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    const value = `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
    ctrl.setValue(value, { emitEvent: true });
  }
}
